import { Skeleton } from '@h2hspark/ui';
import * as React from 'react';

import { Sections } from './sections-panel';
import { pageSections, text, type PublishedContent } from './types';

/**
 * A published marketing page (`CMS-009`).
 *
 * Its metadata comes from the entry rather than from the shell, so each page carries its own
 * title and description — which is the difference between a site that can be shared and one where
 * every link previews identically.
 */
export function CmsPage({ page }: { readonly page: PublishedContent }): React.ReactElement {
  const seo = (page.data['seo'] ?? {}) as Record<string, unknown>;
  const title = text(seo as never, 'metaTitle') || text(page.data, 'title');
  const description = text(seo as never, 'metaDescription') || text(page.data, 'summary');
  const noIndex = seo['noIndex'] === true;

  useDocumentMetadata({ title, description, noIndex });

  return <Sections sections={pageSections(page.data)} />;
}

export function CmsPageSkeleton(): React.ReactElement {
  return (
    <div className="mx-auto max-w-5xl space-y-6 px-6 py-16">
      <Skeleton className="mx-auto h-12 w-2/3" />
      <Skeleton className="mx-auto h-5 w-1/2" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/**
 * Set the tab title, the description and — when asked — a `noindex` directive.
 *
 * Written by hand rather than with a helmet library because the site renders one page at a time
 * and the whole requirement is three tags. The cleanup matters more than the set: leaving a
 * previous page's description behind is how a shared link describes the wrong page.
 */
export function useDocumentMetadata({
  title,
  description,
  noIndex,
}: {
  readonly title: string;
  readonly description: string;
  readonly noIndex: boolean;
}): void {
  React.useEffect(() => {
    const previousTitle = document.title;
    // Only suffix a title that does not already say it. The home page's own title is
    // "H2HSpark — meet the right people at every event", and appending the brand to that produced
    // "… — H2HSpark — H2HSpark" in the tab.
    if (title !== '') {
      document.title = title.includes('H2HSpark') ? title : `${title} — H2HSpark`;
    }

    const descriptionTag = upsertMeta('name', 'description', description);
    const robots = noIndex ? upsertMeta('name', 'robots', 'noindex') : null;

    return () => {
      document.title = previousTitle;
      descriptionTag?.remove();
      robots?.remove();
    };
  }, [title, description, noIndex]);
}

/** Add a meta tag this component owns, or nothing when there is no content for it. */
function upsertMeta(attribute: string, name: string, content: string): HTMLMetaElement | null {
  if (content === '') return null;
  const element = document.createElement('meta');
  element.setAttribute(attribute, name);
  element.setAttribute('content', content);
  document.head.appendChild(element);
  return element;
}
